import { WorkGroupActivityUserTableStore } from './work-group-activity-user-table.store';
import { fb } from '../../../../../modules/shared/shared.global-service';
import { Component, effect, inject, input, signal, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { NumberInputComponent } from '../../../../kit/input/text-input/number-input.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { UserChipComponent } from '../../../../kit/user-chip/user-chip.component';
import { UserChipRenderTypeEnum } from '../../../../kit/user-chip/user-chip.component.types';
import { UserGridItemObject, WorkOfferObject } from '@it2go/types';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import { compareArrays } from '@libs/types/src/utils/array.utils';
import { CardTableComponent } from '../../../../../modules/shared/components/kit/table/card-table.component';

@Component({
    selector: 'app-work-group-activity-user-table',
    templateUrl: './work-group-activity-user-table.component.html',
    styleUrl: './work-group-activity-user-table.component.scss',
    standalone: true,
    providers: [WorkGroupActivityUserTableStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NumberInputComponent,
        PrimeTemplate,
        UserChipComponent,
        CardTableComponent,
    ],
})
export class WorkGroupActivityUserTableComponent {

    public readonly store = inject(WorkGroupActivityUserTableStore);
    public users = input<UserGridItemObject[]>([]);
    public maximumDuration = input<number, any>(0, { transform: v => Number(v || 0) });
    public minimumDurations = input<Record<string, number>>({});
    public defaultValues = input<WorkOfferObject[], WorkOfferObject[] | null | undefined>([], { transform: v => v || [] });
    public disabled = input(false);

    protected _users = signal<UserGridItemObject[]>([]);
    protected applications = viewChildren<NumberInputComponent>('applications');
    protected durationNotes = viewChildren<NumberInputComponent>('durationNote');
    protected durationPreparations = viewChildren<NumberInputComponent>('durationPreparation');
    protected durationSelfReflections = viewChildren<NumberInputComponent>('durationSelfReflection');

    protected lastMaximum = 0;

    protected form = fb.group({});

    protected prefix = 'workData.groupActivity.';
    protected columns = [
        { name: 'user' },
        { name: 'applications' },
        { name: 'durationNote' },
        { name: 'durationPreparation' },
        { name: 'durationSelfReflection' },
    ];

    constructor() {
        effect(() => {
            const defaultApplication = this.maximumDuration();
            const applications = this.applications();
            applications.forEach((it) => {
                const current = it._form().getRawValue().applications;
                if (!current || current == this.lastMaximum) {
                    it.setValue(defaultApplication);
                }
            });
            this.lastMaximum = defaultApplication;
        });

        effect(() => {
            const newUsers = this.users().map((it) => it.id);
            const oldUsers = this._users().map((it) => it.id);
            const diff = compareArrays(newUsers, oldUsers);
            if (diff.isSame) return;

            this._users.set(this.users());
        });

        const setDbValues = effect(() => {
            const values = this.defaultValues();
            const users = this._users();

            const { applications, durationNotes, durationPreparations, durationSelfReflections } = this.inputs();
            if (!values || !users || !values.length || values.length != users.length || values.length != durationSelfReflections.length) return;

            values.forEach((it) => {
                const application = applications.find((ij) => ij.attributes().userId == it.users?.[0] || '');
                const durationNote = durationNotes.find((ij) => ij.attributes().userId == it.users?.[0] || '');
                const durationPreparation = durationPreparations.find((ij) => ij.attributes().userId == it.users?.[0] || '');
                const durationSelfReflection = durationSelfReflections.find((ij) => ij.attributes().userId == it.users?.[0] || '');

                application?.setValue(it.applications);
                durationNote?.setValue(it.durationNote);
                durationPreparation?.setValue(it.durationPreparation);
                durationSelfReflection?.setValue(it.durationSelfReflection);
            });

            setDbValues.destroy();
        });
    }

    public getWorkOffers(): WorkOfferUpsertInput[] {
        const { applications, durationNotes, durationPreparations, durationSelfReflections } = this.inputs();

        return this._users().map((user) => {
            const application = applications.find((it) => it.attributes().userId == user.id);
            const durationNote = durationNotes.find((it) => it.attributes().userId == user.id);
            const durationPreparation = durationPreparations.find((it) => it.attributes().userId == user.id);
            const durationSelfReflection = durationSelfReflections.find((it) => it.attributes().userId == user.id);

            return <WorkOfferUpsertInput>{
                executions: 1,
                applications: application?._form().getRawValue().applications || 0,
                durationNote: durationNote?._form().getRawValue().durationNote || 0,
                durationPreparation: durationPreparation?._form().getRawValue().durationPreparation || 0,
                durationSelfReflection: durationSelfReflection?._form().getRawValue().durationSelfReflection || 0,
                users: [user.id],
            };
        });
    }

    private inputs() {
        const applications = this.applications();
        const durationNotes = this.durationNotes();
        const durationPreparations = this.durationPreparations();
        const durationSelfReflections = this.durationSelfReflections();

        return { applications, durationNotes, durationPreparations, durationSelfReflections };
    }

    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
